import { createContext, useContext, useState, useEffect } from 'react';
import { getUserPreferences } from '../lib/api';

const DisplayPreferencesContext = createContext(null);

export const useDisplayPreferences = () => {
  const context = useContext(DisplayPreferencesContext);
  if (!context) {
    throw new Error('useDisplayPreferences must be used within DisplayPreferencesProvider');
  }
  return context;
};

export const DisplayPreferencesProvider = ({ children }) => {
  const [preferences, setPreferences] = useState({
    date_format: 'MM/DD/YYYY',
    currency: 'USD',
    table_density: 'comfortable',
    show_thousands_separator: true,
    always_show_decimals: true,
    theme: 'system', // New: light, dark, system
    font_size: 'medium', // New: small, medium, large
    compact_mode: false // New: reduces whitespace
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      // First try to load from localStorage (immediate)
      const localPrefs = localStorage.getItem('displayPreferences');
      if (localPrefs) {
        setPreferences(JSON.parse(localPrefs));
      }

      // Then fetch from API (authoritative source)
      const { data } = await getUserPreferences();
      if (data?.display_preferences) {
        setPreferences(data.display_preferences);
        localStorage.setItem('displayPreferences', JSON.stringify(data.display_preferences));
      }
    } catch (error) {
      console.log('Using default display preferences');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    
    // CRITICAL: Parse date string directly to avoid timezone conversion
    // Date comes from backend as "YYYY-MM-DD" string
    // DO NOT use new Date() which applies timezone conversion
    
    let year, month, day;
    
    if (typeof date === 'string') {
      // Parse "YYYY-MM-DD" or "YYYY/MM/DD" format
      const parts = date.split(/[-/]/);
      if (parts.length === 3) {
        year = parts[0];
        month = parts[1];
        day = parts[2];
      } else {
        // Fallback to Date object if format is unexpected
        const d = new Date(date + 'T12:00:00'); // Add noon time to avoid timezone issues
        day = String(d.getDate()).padStart(2, '0');
        month = String(d.getMonth() + 1).padStart(2, '0');
        year = d.getFullYear();
      }
    } else {
      // If it's already a Date object
      const d = new Date(date);
      day = String(d.getDate()).padStart(2, '0');
      month = String(d.getMonth() + 1).padStart(2, '0');
      year = d.getFullYear();
    }
    
    return preferences.date_format === 'DD/MM/YYYY' 
      ? `${day}/${month}/${year}`
      : `${month}/${day}/${year}`;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '';
    
    const symbols = {
      USD: '$',
      EUR: '€',
      GBP: '£'
    };

    const formatted = preferences.show_thousands_separator
      ? amount.toLocaleString('en-US', {
          minimumFractionDigits: preferences.always_show_decimals ? 2 : 0,
          maximumFractionDigits: 2
        })
      : amount.toFixed(preferences.always_show_decimals ? 2 : 0);

    return `${symbols[preferences.currency] || '$'}${formatted}`;
  };

  const formatNumber = (number) => {
    if (number === null || number === undefined) return '';
    
    return preferences.show_thousands_separator
      ? number.toLocaleString('en-US', {
          minimumFractionDigits: preferences.always_show_decimals ? 2 : 0,
          maximumFractionDigits: 2
        })
      : number.toFixed(preferences.always_show_decimals ? 2 : 0);
  };

  const getTableDensityClass = () => {
    const densityMap = {
      compact: 'py-2',
      comfortable: 'py-3',
      spacious: 'py-4'
    };
    return densityMap[preferences.table_density] || 'py-3';
  };

  const getFontSizeClass = () => {
    const fontMap = {
      small: 'text-sm',
      medium: 'text-base',
      large: 'text-lg'
    };
    return fontMap[preferences.font_size] || 'text-base';
  };

  const getCompactClass = () => {
    return preferences.compact_mode ? 'space-y-4' : 'space-y-8';
  };

  const value = {
    preferences,
    loading,
    formatDate,
    formatCurrency,
    formatNumber,
    getTableDensityClass,
    getFontSizeClass,
    getCompactClass,
    refreshPreferences: loadPreferences
  };

  return (
    <DisplayPreferencesContext.Provider value={value}>
      {children}
    </DisplayPreferencesContext.Provider>
  );
};
